import './code-scanner.scss';

import { createElementFromHTML, tryParseJSON } from '../../js/helper';

import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { AjaxLoader } from '../../js/ajax-loader';
import QrScanner from 'qr-scanner';

class CodeScanner {
    constructor ($codeScanner, options) {
        const defaults = {
            initAttr: 'data-productsupport-filter',
            fieldAttr: 'data-input',
            filterAttr: 'filter',
            resultAttr: 'result',
            resetAttr: 'reset',
            loader: null,
            onSubmitted: null
        };

        this.settings = Object.assign({}, defaults, options);

        this.$codeScanner = $codeScanner;
        this.licenseKey = $codeScanner.getAttribute('data-apikey');
        this.picker = undefined;

        this.$startButton = this.$codeScanner.querySelector('[data-code-scanner="start"]');
        this.$closeButton = this.$codeScanner.querySelector('[data-code-scanner="close"]');
        this.$changeCam = this.$codeScanner.querySelector('[data-code-scanner="change-cam"]');
        this.$okButton = this.$codeScanner.querySelector('[data-code-scanner="ok"]');
        this.$productButton = this.$codeScanner.querySelector('[data-code-scanner="product"]');

        this.$scannerWrapper = this.$codeScanner.querySelector('[data-code-scanner="scanner-wrapper"]');
        this.$videoStream = this.$codeScanner.querySelector('[data-code-scanner="stream"]');
        this.$qrScanner = null;
        this.checkUri = this.$codeScanner.getAttribute('data-url');
        this.targetName = this.$codeScanner.getAttribute('data-target');
        this.targetMatNr = this.$codeScanner.getAttribute('data-target-matnr');
        this.targetDtswissId = this.$codeScanner.getAttribute('data-target-dtswiss-id');

        this.$resultOverlay = this.$codeScanner.querySelector('[data-code-scanner="result-overlay"]');
        this.$resultText = this.$codeScanner.querySelector('[data-code-scanner="result-text"]');

        this.$header = document.querySelector('[data-header="root"]');

        this.scanningMessage = this.$codeScanner.getAttribute('data-message-scanning');
        this.successMessage = this.$codeScanner.getAttribute('data-message-success');
        this.errorMessage = this.$codeScanner.getAttribute('data-message-error');

        this.allCameras = [];
        this.currentCameraIndex = 'environment';

        if (this.settings.loader !== null) {
            this.$loader = createElementFromHTML(this.settings.loader);
        }
    }

    init () {
        this.bindEvents();
    }

    bindEvents () {
        if (this.$startButton) {
            this.$startButton.addEventListener('click', (e) => {
                this.initCodeScanner();
                this.showScanner();
                e.preventDefault();
            });
        }

        if (this.$closeButton) {
            this.$closeButton.addEventListener('click', () => {
                this.hideScanner();
            });
        }

        if (this.$okButton) {
            this.$okButton.addEventListener('click', () => {
                this.hideResult();
            });
        }

        if (this.$changeCam) {
            this.$changeCam.addEventListener('click', () => {
                this.setActiveCamera();
            });
        }
    }

    initCodeScanner () {
        this.$qrScanner = new QrScanner(this.$videoStream, result => {
            if (result) {
                this.showResult(result.data);
                this.$qrScanner.stop();
            }
        }, {
            calculateScanRegion: (video) => {
                const videoWidth = video.videoWidth;
                const videoHeight = video.videoHeight;
                const size = 200;
                const x = (videoWidth - size) / 2;
                const y = (videoHeight - size) / 2;
                return { x, y, width: size, height: size };
            },
            preferredCamera: this.currentCameraIndex,
            highlightScanRegion: true,
            highlightCodeOutline: true
        });

        this.$qrScanner.setInversionMode('both');

        this.$qrScanner.start().then(() => {
            QrScanner.listCameras(true).then(cameras => {
                cameras.forEach(camera => {
                    this.allCameras.push(camera);
                });

                if (this.allCameras.length > 1) {
                    this.$changeCam.classList.add('code-scanner__change-cam--is-active');
                }
            });
        });
    }

    setActiveCamera () {
        if (this.currentCameraIndex === 'environment') {
            this.currentCameraIndex = 'user';
        } else {
            this.currentCameraIndex = 'environment';
        }

        this.$qrScanner.setCamera(this.currentCameraIndex);
    }

    showScanner () {
        this.$scannerWrapper.classList.add('code-scanner__scanner-wrapper--visible');
        disableBodyScroll(this.$scannerWrapper);

        document.body.classList.add('has--codescanner');
        if (this.$header) {
            this.$header.classList.add('has--overlay');
        }
    }

    hideScanner () {
        this.$scannerWrapper.classList.remove('code-scanner__scanner-wrapper--visible');
        enableBodyScroll(this.$scannerWrapper);

        if (this.$videoStream != null) {
            const scanContextBox = this.$scannerWrapper.querySelector('.scan-region-highlight');
            scanContextBox.remove();
            this.$qrScanner.destroy();
            this.$qrScanner = null;
            this.allCameras = [];
        }

        document.body.classList.remove('has--codescanner');
        if (this.$header) {
            this.$header.classList.remove('has--overlay');
        }
    }

    showResult (message) {
        let url = message;
        const testUrlRegex = /^(?:https?:\/\/)?(?:[\w.-]*dtswiss[\w.-]*)\/id\/[^\s]+$/i;
        const checkUrl = testUrlRegex.test(url);

        if (!/^https?:\/\//i.test(url)) {
            url = 'https://' + url;
        }

        if (checkUrl === true && this.targetName) {
            const $targetField = document.querySelector(this.targetName);
            if ($targetField) {
                const id = message.split('/').pop().slice(0, 7);
                this.checkCode(id, (resultJSON) => {
                    $targetField.value = resultJSON.product;

                    if (this.targetMatNr && this.targetDtswissId) {
                        const $targetMatNrField = document.querySelector(this.targetMatNr);
                        const $targetDtswissIdField = document.querySelector(this.targetDtswissId);

                        if ($targetMatNrField && $targetDtswissIdField) {
                            $targetMatNrField.value = resultJSON.matnr;
                            $targetDtswissIdField.value = resultJSON.dswiss_id;
                        }
                    }
                });
            }
            setTimeout(() => {
                this.hideScanner();
            }, 100);
        } else if (checkUrl === true) {
            location.href = url;
        } else {
            this.$resultOverlay.classList.add('is--visible');
            this.$resultText.innerHTML = this.errorMessage + '<br />' + url;
            this.$resultOverlay.classList.add('has--error');
        }
    }

    hideResult () {
        this.$resultOverlay.classList.remove('is--visible');
        this.$resultOverlay.classList.remove('has--success');
        this.$resultOverlay.classList.remove('has--error');

        if (this.$videoStream != null) {
            this.$qrScanner.start();
        }
    }

    checkCode (code, callback) {
        this.checkCodeResult = new AjaxLoader(this.checkUri + code, {
            onSuccess: (resultText) => {
                const result = tryParseJSON(resultText);

                if (result !== false) {
                    callback(result);
                }
            }
        });
    }
}

export { CodeScanner };

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $codeScanner = $context.querySelector('[data-code-scanner="root"]');

        if ($codeScanner) {
            const codeScanner = new CodeScanner($codeScanner, {
                loader: window.OPTIONS.loader
            });
            codeScanner.init();
        }
    }
});
